import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { resolveImageUrl } from '../lib/api';
import { useLang } from '../hooks/useLang';
import { pickLocalized } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FeaturedProjects = () => {
  const { t } = useTranslation();
  const { lang, routes } = useLang();
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeaturedProjects = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/projects`);
        if (!response.ok) throw new Error('Failed to fetch projects');
        const data = await response.json();
        setFeaturedProjects(data.filter(p => p.featured));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching featured projects:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchFeaturedProjects();
  }, []);

  if (loading) {
    return (
      <section className="py-24 px-8 lg:px-16 bg-white">
        <div className="max-w-[1920px] mx-auto">
          <h2 className="text-4xl lg:text-5xl font-light mb-6">{t('home.philosophyTitle')}</h2>
          <p className="text-center text-gray-500">{t('common.loading')}</p>
        </div>
      </section>
    );
  }
  if (error) return null;

  return (
    <section className="py-24 px-8 lg:px-16 bg-white">
      <div className="max-w-[1920px] mx-auto">
        <h2 className="text-4xl lg:text-5xl font-light mb-6">{t('home.philosophyTitle')}</h2>
        <p className="text-lg font-light text-gray-600 mb-16 max-w-3xl">{t('home.philosophyIntro')}</p>

        <div className="space-y-24">
          {featuredProjects.map((project, index) => {
            const title = pickLocalized(project, 'title', lang);
            const description = pickLocalized(project, 'description', lang);
            const firstParagraph = Array.isArray(description) ? (description[0] || '') : (description || '');
            const image = project.images && project.images.length > 0 ? project.images[0].url : '';
            return (
              <div key={project.slug} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                <div className="w-full lg:w-1/2">
                  <div className="overflow-hidden group bg-white">
                    <img src={resolveImageUrl(image)} alt={title}
                      className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-105" />
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-3xl lg:text-4xl font-light mb-6 leading-tight">{title}</h3>
                  <p className="text-base font-light text-gray-700 leading-relaxed mb-8">{firstParagraph}</p>
                  <Link to={routes.project(project.slug)}
                    className="inline-flex items-center gap-2 text-sm font-light tracking-wide text-[#d94e47] hover:gap-4 transition-all duration-300 border-b border-[#d94e47]">
                    {t('common.discoverProject')}
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;
